import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Checkbox, FormControlLabel, Typography, useMediaQuery } from '@mui/material';

import { EditTextFieldRow } from '@/shared/UI/styled/global';
import { UseAppDispatch } from '@/shared/store';
import { loadCargoType, loadHosRules, loadRestBreak, loadRestart } from '@/shared/store/actions/DriverActionCreators';
import { CompanySettingsSchema } from '@/shared/consts/schema';

import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import SelectControl from '@/shared/UI/elements/form/Select';
import { GridInfoModelSettings, GridInfoSettings } from '@/shared/models/company.model';
import theme from '@/app/providers/theme';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;


const EditCompanyFormSettings = forwardRef(({ data }: any, ref) => {
  const appDispatch = UseAppDispatch();

  const mediaQueryDownSM = useMediaQuery(theme.breakpoints.down(800));

  const hosRules = useSelector((state: any) => state.driver.hosRules);
  const cargoType = useSelector((state: any) => state.driver.cargoType);
  const restart = useSelector((state: any) => state.driver.restart);
  const restBreak = useSelector((state: any) => state.driver.restBreak);

  const [model, setModel] = useState<GridInfoSettings>(GridInfoModelSettings());

  const [hosRulesValue, setHosRulesValue] = useState<string>('');
  const [cargoTypeValue, setCargoTypeValue] = useState<string>('');
  const [restartValue, setRestartValue] = useState<string>('');
  const [restBreakValue, setRestBreakValue] = useState<string>('');

  const [exemptDriver, setExemptDriver] = useState<boolean>(model?.exemptDriver);
  const [shortHaulException, setShortHaulException] = useState<boolean>(model?.shortHaulException);
  const [allowPersonalUse, setAllowPersonalUse] = useState<boolean>(model?.allowPersonalUse);
  const [allowYardMoves, setAllowYardMoves] = useState<boolean>(model.allowYardMoves);

  const {
    control,
    trigger,
    setValue,
    getValues,
    reset,
    formState: { isValid },
  } = useForm<GridInfoSettings>({
    mode: 'onChange',
    resolver: yupResolver(CompanySettingsSchema),
  });

  useImperativeHandle(ref, () => ({
    async submit () {
      await trigger();
      if (isValid) {
        return Object.assign({}, getValues());
      }
    },
  }));

  useEffect(() => {
    getCompanyDataSources();
  }, []);

  useEffect(() => {
    if (data) {
      setModel(data);
      reset(data);
    }
  }, [data]);

  useEffect(() => {
    if (model) {
      if (hosRules) prefillHosRules(model.hosRules);
      if (cargoType) prefillCargoType(model.cargoType);
      if (restart) prefillRestart(model.restart);
      if (restBreak) prefillRestBreak(model.restBreak);

      setExemptDriver(model.exemptDriver);
      setShortHaulException(model.shortHaulException);
      setAllowPersonalUse(model.allowPersonalUse);
      setAllowYardMoves(model.allowYardMoves);
    }
  }, [hosRules, cargoType, restart, restBreak, model]);


  const getCompanyDataSources = async () => {
    await appDispatch(loadHosRules());
    await appDispatch(loadCargoType());
    await appDispatch(loadRestart());
    await appDispatch(loadRestBreak());
  };

  const prefillHosRules = (rule: number | string) => {
    const hosRulesValue = hosRules?.find((item: any) => item.value === rule)?.value || '';
    setHosRulesValue(hosRulesValue);
  };

  const prefillCargoType = (cargoTypeVal: number | string) => {
    const cargoTypeValue = cargoType?.find((item: any) => item.value === cargoTypeVal)?.value || '';
    setCargoTypeValue(cargoTypeValue);
  };

  const prefillRestart = (restartVal: number | string) => {
    const restartValue = restart?.find((item: any) => item.value === restartVal)?.value || '';
    setRestartValue(restartValue);
  };

  const prefillRestBreak = (restBreakVal: number | string) => {
    const restBreakValue = restBreak?.find((item: any) => item.value === restBreakVal)?.value || '';
    setRestBreakValue(restBreakValue);
  };

  return (
    <Box
      component="form"
      sx={{ width: '100%' }}
    >
      <Typography sx={{ marginBottom: '20px' }} variant="h4">
        Default Driver Log Settings
      </Typography>

      <EditTextFieldRow>
        <Controller
          control={control}
          name="hosRules"
          rules={{
            required: true,
          }}
          render={({ fieldState: { error } }) => (
            <SelectControl
              placeholder="HOS Rules"
              data={hosRules || []}
              value={hosRulesValue}
              handleChange={(val: any, newVal: any) => {
                setValue('hosRules', newVal?.props?.value);
                setHosRulesValue(newVal?.props?.value);
                trigger();
              }}
              styles={{ maxWidth: '297px', marginRight: '20px', width: '100%' }}
              error={error !== undefined}
            />
          )}
        />
        <Controller
          control={control}
          name="cargoType"
          rules={{
            required: true,
          }}
          render={({ fieldState: { error } }) => (
            <SelectControl
              placeholder="Cargo Type"
              data={cargoType || []}
              value={cargoTypeValue}
              handleChange={(val: any, newVal: any) => {
                setValue('cargoType', newVal?.props?.value);
                setCargoTypeValue(newVal?.props?.value);
                trigger();
              }}
              styles={{ maxWidth: '297px', marginRight: '20px', width: '100%' }}
              error={error !== undefined}
            />
          )}
        />
      </EditTextFieldRow>

      <EditTextFieldRow>
        <Controller
          control={control}
          name="restart"
          rules={{
            required: true,
          }}
          render={({ fieldState: { error } }) => (
            <SelectControl
              placeholder="Restart"
              data={restart || []}
              value={restartValue}
              handleChange={(val: any, newVal: any) => {
                setValue('restart', newVal?.props?.value);
                setRestartValue(newVal?.props?.value);
                trigger();
              }}
              styles={{ maxWidth: '297px', marginRight: '20px', width: '100%' }}
              error={error !== undefined}
            />
          )}
        />
        <Controller
          control={control}
          name="restBreak"
          rules={{
            required: true,
          }}
          render={({ fieldState: { error } }) => (
            <SelectControl
              placeholder="Rest Break"
              data={restBreak || []}
              value={restBreakValue}
              handleChange={(val: any, newVal: any) => {
                setValue('restBreak', newVal?.props?.value);
                setRestBreakValue(newVal?.props?.value);
                trigger();
              }}
              styles={{ maxWidth: '297px', marginRight: '20px', width: '100%' }}
              error={error !== undefined}
            />
          )}
        />
      </EditTextFieldRow>

      <EditTextFieldRow
        sx={{
          [theme.breakpoints.down(800)]: {
            marginBottom: 0,
          }
        }}>
        <Controller
          control={control}
          name="exemptDriver"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  {...field}
                  onChange={(_, newValue) => {
                    setValue('exemptDriver', newValue);
                    setExemptDriver(newValue);
                  }}
                  checked={exemptDriver || false}
                />
              }
              style={{ marginLeft: 0, width: mediaQueryDownSM ? '100%' : '33%' }}
              label="Exempt Driver"
            />
          )}
        />
        <Controller
          control={control}
          name="shortHaulException"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  {...field}
                  onChange={(_, newValue) => {
                    setValue('shortHaulException', newValue);
                    setShortHaulException(newValue);
                  }}
                  checked={shortHaulException || false}
                />
              }
              style={{ marginLeft: 0, width: mediaQueryDownSM ? '100%' : '33%' }}
              label="Short-Haul Exception"
            />
          )}
        />
        <Controller
          control={control}
          name="allowPersonalUse"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  {...field}
                  onChange={(_, newValue) => {
                    setValue('allowPersonalUse', newValue);
                    setAllowPersonalUse(newValue);
                  }}
                  checked={allowPersonalUse || false}
                />
              }
              style={{ marginLeft: 0, width: mediaQueryDownSM ? '100%' : '33%' }}
              label="Allow Personal Use"
            />
          )}
        />
      </EditTextFieldRow>

      <EditTextFieldRow>
        <Controller
          control={control}
          name="allowYardMoves"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  {...field}
                  onChange={(_, newValue) => {
                    setValue('allowYardMoves', newValue);
                    setAllowYardMoves(newValue);
                  }}
                  checked={allowYardMoves || false}
                />
              }
              style={{ marginLeft: 0, width: mediaQueryDownSM ? '100%' : '33%' }}
              label="Allow Yard Moves"
            />
          )}
        />
      </EditTextFieldRow>
    </Box>
  );
});

EditCompanyFormSettings.displayName = 'EditCompanyFormSettings';

export default EditCompanyFormSettings;
